'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import type { User, Session } from '@supabase/supabase-js';
import type { OwnerProfile, StoreData, SubscriptionData } from '@/lib/auth';
import { getOwnerProfile, getOwnerStore, getOwnerSubscription } from '@/lib/auth';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  ownerProfile: OwnerProfile | null;
  store: StoreData | null;
  subscription: SubscriptionData | null;
  signUp: (email: string, password: string, metadata?: Record<string, string>) => Promise<unknown>;
  signIn: (email: string, password: string) => Promise<unknown>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<User | null>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<OwnerProfile | null>;
  refreshOwnerData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [ownerProfile, setOwnerProfile] = useState<OwnerProfile | null>(null);
  const [store, setStore] = useState<StoreData | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const supabase = createClient();

  const loadOwnerData = useCallback(async (userId: string) => {
    try {
      // store + subscription are created by the DB trigger (SECURITY DEFINER).
      // We only read here — never insert from the client.
      const [profile, storeData, subData] = await Promise.all([
        getOwnerProfile(userId),
        getOwnerStore(userId),
        getOwnerSubscription(userId),
      ]);
      setOwnerProfile(profile);
      setStore(storeData);
      setSubscription(subData);
    } catch (err) {
      console.error('[Loyzen] Failed to load owner data:', err);
    }
  }, []);

  const refreshOwnerData = useCallback(async () => {
    if (user) {
      await loadOwnerData(user.id);
    }
  }, [user, loadOwnerData]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadOwnerData(session.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    const {
      data: { subscription: authSubscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadOwnerData(session.user.id).finally(() => setLoading(false));
      } else {
        setOwnerProfile(null);
        setStore(null);
        setSubscription(null);
        setLoading(false);
      }
    });

    return () => authSubscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const signUp = async (email: string, password: string, metadata: Record<string, string> = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName ?? '',
          first_name: metadata?.firstName ?? '',
          avatar_url: metadata?.avatarUrl ?? '',
        },
        emailRedirectTo: `${process.env.NEXT_PUBLIC_SITE_URL ?? ''}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => {
    return user?.email_confirmed_at != null;
  };

  const getUserProfile = async () => {
    if (!user) return null;
    return getOwnerProfile(user.id);
  };

  const value: AuthContextValue = {
    user,
    session,
    loading,
    ownerProfile,
    store,
    subscription,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshOwnerData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
